import { MessagePreview } from '@botpress/webchat'
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Input,
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Switch,
} from '@bpinternal/ui-kit-next'
import { useMemo, type FC } from 'react'
import { getUseConfigStore } from '../../stores/useConfigStore'
import { ConfigSection } from './GeneralConfig'
import { CircleHelp } from 'lucide-react'
import WebchatComponentPreview from '../elements/WebchatComponentPreview'

type ProactiveBubbleSectionProps = {
  botId: string
  workspaceId: string
}

export const ProactiveBubbleSection: FC<ProactiveBubbleSectionProps> = ({ botId, workspaceId }) => {
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])
  const botName = useConfigStore((state) => state.botName)
  const botAvatar = useConfigStore((state) => state.botAvatar)
  const proactiveMessageEnabled = useConfigStore((state) => state.proactiveMessageEnabled)
  const proactiveMessage = useConfigStore((state) => state.proactiveBubbleMessage)
  const triggerType = useConfigStore((state) => state.proactiveBubbleTriggerType)
  const delayTime = useConfigStore((state) => state.proactiveBubbleDelayTime)
  const setProactiveMessageEnabled = useConfigStore((state) => state.setProactiveMessageEnabled)
  const setProactiveMessage = useConfigStore((state) => state.setProactiveBubbleMessage)
  const setTriggerType = useConfigStore((state) => state.setProactiveBubbleTriggerType)
  const setDelayTime = useConfigStore((state) => state.setProactiveBubbleDelayTime)

  return (
    <>
      <ConfigSection title="Proactive Message" description="A short message that appears above the chat bubble.">
        <div className="flex flex-col gap-6">
          <div className="flex items-center justify-between ">
            <div className="flex gap-1">
              <Label className="text-muted-foreground">Enable</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelp className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Enable the proactive message bubble that engages users at the right moment.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch checked={proactiveMessageEnabled} onCheckedChange={setProactiveMessageEnabled} />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="font-small text-sm text-muted-foreground">Proactive Message</Label>
            <Textarea
              value={proactiveMessage}
              onChange={(e) => setProactiveMessage(e.target.value)}
              placeholder="Enter your proactive message..."
              className="min-h-[80px] resize-none"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Trigger Type</Label>
            <Select value={triggerType} onValueChange={(value) => setTriggerType(value as 'afterDelay' | 'userIdle')}>
              <SelectTrigger>
                <SelectValue placeholder="Select trigger type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="afterDelay">After Delay</SelectItem>
                <SelectItem value="userIdle">After User Idle</SelectItem>
              </SelectContent>
            </Select>

            <p className="text-xs text-muted-foreground">
              {triggerType === 'afterDelay'
                ? `The message will appear after ${delayTime} seconds.`
                : `The message will appear after the users idles on a page for ${delayTime} seconds.`}
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-sm font-medium">Delay Time (seconds)</Label>
            <div className="flex items-center gap-2">
              <Input
                value={delayTime}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '') {
                    setDelayTime(0)
                  } else {
                    const numValue = Number(value)
                    if (numValue >= 1) {
                      setDelayTime(numValue)
                    }
                  }
                }}
                min={1}
                className="w-32"
              />
            </div>
          </div>
          <WebchatComponentPreview className="flex items-center justify-center rounded-lg border bg-neutral-50 p-2">
            {/* These dimensions are the hard-coded size of the iframe */}
            <div className="h-[70px] w-[330px]">
              <MessagePreview avatarUrl={botAvatar} description={`${botName ?? 'Your agent'} \u2219 a few moments ago`}>
                {proactiveMessage}
              </MessagePreview>
            </div>
          </WebchatComponentPreview>
        </div>
      </ConfigSection>
    </>
  )
}
